import { buildSearchFilters } from '@/utils/searchAdminForm';
import { quickSetupToVehicleAdminForm } from './vehicleFilters';

export type SearchCategory =
  | 'vehicles'
  | 'real_estate'
  | 'jobs'
  | 'marketplace'
  | 'custom';

export type WizardStep = 1 | 2 | 3 | 4;

export interface QuickSetupFormState {
  category: SearchCategory;
  telegram_chat_id: string;
  name: string;
  source_names: string[];
  run_scrape: boolean;
  search_text: string;
  brand: string;
  model: string;
  year_min: string;
  year_max: string;
  price_min: string;
  price_max: string;
  mileage_min: string;
  mileage_max: string;
  fuel: string;
  transmission: string;
  message_templates: string;
  city: string;
  region: string;
  area_min: string;
  area_max: string;
  rooms_min: string;
  rooms_max: string;
  property_type: 'apartment' | 'house' | '';
  keyword: string;
  location: string;
  remote_only: boolean;
  salary_min: string;
  condition: string;
  custom_filters: string;
}

export const CATEGORY_META: Record<
  SearchCategory,
  { icon: string; title: string; subtitle: string }
> = {
  vehicles: { icon: '🚗', title: 'Vehicles', subtitle: 'Cars, motorcycles, boats' },
  real_estate: { icon: '🏠', title: 'Real Estate', subtitle: 'Apartments, houses, land' },
  jobs: { icon: '💼', title: 'Jobs', subtitle: 'Employment alerts' },
  marketplace: { icon: '🛒', title: 'Marketplace', subtitle: 'General classified listings' },
  custom: { icon: '⚙️', title: 'Custom', subtitle: 'Advanced search' },
};

export const WIZARD_STEPS = [
  { id: 1 as WizardStep, label: 'Category' },
  { id: 2 as WizardStep, label: 'Configure Search' },
  { id: 3 as WizardStep, label: 'Recipient' },
  { id: 4 as WizardStep, label: 'Create Alert' },
];

export function createEmptyForm(category: SearchCategory = 'vehicles'): QuickSetupFormState {
  return {
    category,
    telegram_chat_id: '',
    name: '',
    source_names: [],
    run_scrape: true,
    search_text: '',
    brand: '',
    model: '',
    year_min: '',
    year_max: '',
    price_min: '',
    price_max: '',
    mileage_min: '',
    mileage_max: '',
    fuel: '',
    transmission: '',
    message_templates: '',
    city: '',
    region: '',
    area_min: '',
    area_max: '',
    rooms_min: '',
    rooms_max: '',
    property_type: '',
    keyword: '',
    location: '',
    remote_only: false,
    salary_min: '',
    condition: '',
    custom_filters: '',
  };
}

/** Map wizard category to API-registered category id. */
export function categoryForApi(category: SearchCategory): string {
  const map: Record<SearchCategory, string> = {
    vehicles: 'vehicles',
    real_estate: 'apartments',
    jobs: 'hot_topics',
    marketplace: 'hot_topics',
    custom: 'hot_topics',
  };
  return map[category];
}

function numOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

export function buildFilters(form: QuickSetupFormState): Record<string, unknown> {
  const f: Record<string, unknown> = { setup_category: form.category };

  switch (form.category) {
    case 'vehicles': {
      return {
        setup_category: 'vehicles',
        ...buildSearchFilters(quickSetupToVehicleAdminForm(form)),
      };
    }
    case 'real_estate': {
      if (form.search_text.trim()) f.search_text = form.search_text.trim();
      if (form.city.trim()) f.city = form.city.trim();
      if (form.region.trim()) f.region = form.region.trim();
      const priceMin = numOrUndefined(form.price_min);
      const priceMax = numOrUndefined(form.price_max);
      const areaMin = numOrUndefined(form.area_min);
      const areaMax = numOrUndefined(form.area_max);
      const roomsMin = numOrUndefined(form.rooms_min);
      const roomsMax = numOrUndefined(form.rooms_max);
      if (priceMin != null) f.price_min = priceMin;
      if (priceMax != null) f.price_max = priceMax;
      if (areaMin != null) f.sqm_min = areaMin;
      if (areaMax != null) f.sqm_max = areaMax;
      if (roomsMin != null) f.rooms_min = roomsMin;
      if (roomsMax != null) f.rooms_max = roomsMax;
      if (form.property_type) f.property_type = form.property_type;
      break;
    }
    case 'jobs': {
      if (form.keyword.trim()) f.keyword = form.keyword.trim();
      if (form.location.trim()) f.location = form.location.trim();
      if (form.remote_only) f.remote_only = true;
      const salaryMin = numOrUndefined(form.salary_min);
      if (salaryMin != null) f.salary_min = salaryMin;
      break;
    }
    case 'marketplace': {
      if (form.search_text.trim()) f.search_text = form.search_text.trim();
      const priceMin = numOrUndefined(form.price_min);
      const priceMax = numOrUndefined(form.price_max);
      if (priceMin != null) f.price_min = priceMin;
      if (priceMax != null) f.price_max = priceMax;
      if (form.condition.trim()) f.condition = form.condition.trim();
      break;
    }
    case 'custom': {
      if (form.search_text.trim()) f.search_text = form.search_text.trim();
      if (form.custom_filters.trim()) {
        try {
          const extra = JSON.parse(form.custom_filters) as Record<string, unknown>;
          Object.assign(f, extra);
        } catch {
          f.custom_filters_raw = form.custom_filters.trim();
        }
      }
      break;
    }
    default:
      break;
  }

  return f;
}
